// Render a thermal receipt as a monochrome BITMAP, exactly like Android does
// (createImageParameterFromText → Canvas/Bitmap → actionPrintImage). We then hand the printer an
// IMAGE, not HTML — deterministic, unlike QZ's headless HTML renderer (which prints blank/garbage
// intermittently on thermal). buildReceiptLines/buildTestLines are pure; renderLinesToImage needs a
// browser <canvas>.

export function paperWidthDots(mm) {
  return Math.max(200, Math.round((Number(mm) || 72) * 8)); // Star thermal ≈ 8 dots/mm (203 dpi): 80mm→576
}

const money = (n) => "$" + (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);
const S = (v) => String(v == null ? "" : v);

// Ordered line descriptors from the receipt model (pure → unit-testable).
export function buildReceiptLines(m) {
  const L = [];
  const center = (text, o) => L.push({ type: "center", text: S(text), ...(o || {}) });
  const lr = (l, r, o) => L.push({ type: "lr", left: S(l), right: S(r), ...(o || {}) });
  const div = () => L.push({ type: "divider" });
  const gap = (h) => L.push({ type: "gap", h: h || 0.5 });
  const store = m.store || {};
  if (store.name) center(store.name, { size: "lg", bold: true });
  if (store.address) center(store.address);
  if (store.phone) center(store.phone);
  if (m.orderId) center("Order " + m.orderId);
  if (m.dateTime) center(m.dateTime);
  if (m.cashier) center("Cashier: " + m.cashier);
  center("# of Items: " + (m.itemCount || 0));
  div();
  (m.lines || []).forEach((it) => {
    lr((it.qty || 0) + " x " + S(it.name), money(it.price));
    if (it.promo) center(it.promo, { size: "sm" });
  });
  div();
  lr("Sub Total", money(m.subtotal));
  if ((m.discount || 0) > 0) lr("Discount", "-" + money(m.discount));
  const taxLines = m.taxLines || [];
  if (taxLines.length) taxLines.forEach((t) => lr(S(t.name) + (t.percent ? " (" + S(t.percentRaw || t.percent) + "%)" : ""), money(t.amount)));
  else if ((m.tax || 0) > 0) lr("Tax", money(m.tax));
  if ((m.nca || 0) > 0) lr("Non-Cash Adj", money(m.nca));
  if ((m.tip || 0) > 0) lr("Tip", money(m.tip));
  div();
  lr("GRAND TOTAL", money(m.grandTotal), { bold: true, size: "lg" });
  div();
  (m.giftCards || []).forEach((g) => lr("Gift Card (" + S(g.number) + ")", "-" + money(g.amount)));
  if ((m.loyaltySpent || 0) > 0) lr("Loyalty Applied", "-" + money(m.loyaltySpent));
  if ((m.storeCredit || 0) > 0) lr("Store Credit Used", "-" + money(m.storeCredit));
  (m.payments || []).forEach((p) => lr(S(p.label) + (p.last4 ? "-" + p.last4 : ""), money(p.amount)));
  if ((m.cashbackAmt || 0) > 0) lr("Cash Back", money(m.cashbackAmt));
  if ((m.cashPaid || 0) > 0) lr("Cash Paid", money(m.cashPaid));
  if ((m.changeDue || 0) > 0) lr("Change Due", money(m.changeDue), { bold: true });
  gap(0.6);
  center("Thank you!");
  return L;
}

export function buildTestLines(who) {
  return [
    { type: "center", text: "QUICKVEE", size: "lg", bold: true },
    { type: "center", text: "*** TEST PRINT ***" },
    { type: "gap", h: 0.4 },
    { type: "center", text: S(who), size: "sm" },
    { type: "center", text: "If you can read this," },
    { type: "center", text: "printing works." },
    { type: "gap", h: 0.4 },
  ];
}

// Draw the descriptors to a white canvas with solid-black text → PNG data URL. Browser-only.
export function renderLinesToImage(lines, widthDots) {
  const W = Math.max(200, Math.round(widthDots) || 576);
  const base = Math.max(22, Math.round(W * 0.055)); // body font px (~32 @ 576) — matches Android textSize=32
  const sizePx = (s) => (s === "lg" ? Math.round(base * 1.25) : s === "sm" ? Math.round(base * 0.82) : base);
  const lineH = (s) => Math.round(sizePx(s) * 1.4);
  const pad = Math.round(base * 0.6);

  let h = pad;
  for (const ln of lines) {
    if (ln.type === "gap") h += Math.round(base * (ln.h || 0.5));
    else if (ln.type === "divider") h += lineH("sm");
    else h += lineH(ln.size);
  }
  h += pad;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, h);
  ctx.fillStyle = "#000000";
  ctx.strokeStyle = "#000000";
  ctx.textBaseline = "top";
  const emit = (str, x, align, yy) => {
    ctx.textAlign = align;
    ctx.fillText(str, x, yy);
    ctx.strokeText(str, x, yy); // fill + light stroke = fake-bold (like Android isFakeBoldText)
  };

  let y = pad;
  for (const ln of lines) {
    if (ln.type === "gap") {
      y += Math.round(base * (ln.h || 0.5));
      continue;
    }
    if (ln.type === "divider") {
      const yy = y + Math.round(lineH("sm") / 2);
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = Math.max(1, Math.round(base * 0.06));
      ctx.beginPath();
      ctx.moveTo(pad, yy);
      ctx.lineTo(W - pad, yy);
      ctx.stroke();
      y += lineH("sm");
      continue;
    }
    const px = sizePx(ln.size);
    // Thick monospace (like Android's Typeface.MONOSPACE), not thin Courier New.
    ctx.font = (ln.bold ? "bold " : "") + px + "px 'Consolas','DejaVu Sans Mono','Roboto Mono',monospace";
    ctx.lineWidth = Math.max(0.7, px * 0.03);
    if (ln.type === "center") emit(ln.text, Math.round(W / 2), "center", y);
    else if (ln.type === "lr") {
      emit(ln.left, pad, "left", y);
      emit(ln.right, W - pad, "right", y);
    }
    y += lineH(ln.size);
  }

  // Force PURE black text on PURE white — thermal is 1-bit, so any gray anti-alias pixel gets dithered
  // into faint scattered dots (the "faded" look). Threshold high (any non-near-white pixel → solid
  // black) so glyphs — even small ones — burn fully dark, matching Android's bitmap output.
  try {
    const img = ctx.getImageData(0, 0, W, h);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      const v = lum >= 176 ? 255 : 0; // >mid → white, else solid black (source is already bold via fake-bold)
      d[i] = d[i + 1] = d[i + 2] = v;
      d[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
  } catch (e) {
    /* getImageData may throw on a tainted canvas — skip binarize, still prints */
  }
  return canvas.toDataURL("image/png");
}
